import { createContext, useEffect, useMemo, useState } from 'react';
import {
  checkMenus,
  checkCompany,
  checkClaimTabSettings,
} from 'shared/lib/authUtils';

/**
 * PermissionProvider — reactive React projection of the localStorage-backed
 * permission state (SA-1592 Phase A0).
 *
 * Source of truth stays localStorage via `authUtils.js`. This provider is a
 * cached read-only projection: it seeds `menus`, `company`, and
 * `claimTabSettings` once (lazy), then re-reads whenever the underlying storage
 * changes — either from another tab (the native `storage` event) or from the
 * writer in this tab dispatching the custom `permissions:updated` event (see
 * `applyPermissionsFromMe`).
 *
 * It deliberately does NOT call `_invalidateMenusCache` (the writer /
 * setupHttpClient own cache invalidation) and does NOT redirect on revoke
 * (setupHttpClient owns the access-revoke check). It is inert until consumed.
 */

export const PermissionContext = createContext({
  menus: null,
  company: null,
  claimTabSettings: null,
});

// Storage keys whose mutation should trigger a re-read of the projection.
const WATCHED_STORAGE_KEYS = ['menus', 'company', 'claim_tab_settings'];
const PERMISSIONS_UPDATED_EVENT = 'permissions:updated';

const PermissionProvider = ({ children }) => {
  const [menus, setMenus] = useState(() => checkMenus());
  const [company, setCompany] = useState(() => checkCompany());
  const [claimTabSettings, setClaimTabSettings] = useState(() =>
    checkClaimTabSettings()
  );
  useEffect(() => {
    const reread = () => {
      setMenus(checkMenus());
      setCompany(checkCompany());
      setClaimTabSettings(checkClaimTabSettings());
    };

    // Cross-tab: native storage event fires only in OTHER tabs. Re-read only
    // when a watched key changed (event.key is null on storage.clear()).
    const handleStorage = (event) => {
      const isWatchedKey =
        event?.key === null || WATCHED_STORAGE_KEYS.includes(event?.key);
      if (isWatchedKey) reread();
    };

    // Same-tab: the writer dispatches this after rewriting the permission keys
    // (menus / company / claim_tab_settings).
    const handlePermissionsUpdated = () => reread();

    window.addEventListener('storage', handleStorage);
    window.addEventListener(
      PERMISSIONS_UPDATED_EVENT,
      handlePermissionsUpdated
    );

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(
        PERMISSIONS_UPDATED_EVENT,
        handlePermissionsUpdated
      );
    };
  }, []);

  const value = useMemo(
    () => ({ menus, company, claimTabSettings }),
    [menus, company, claimTabSettings]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionProvider;
